import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface Option {
  value: string
  label: string
  rationale?: string
}

interface ScaffoldedOptionsProps {
  options: Option[]
  allowOther: boolean
  onSelect: (value: string, isCustom: boolean) => void
  selected?: string
}

export function ScaffoldedOptions({ options, allowOther, onSelect, selected }: ScaffoldedOptionsProps) {
  const [showCustom, setShowCustom] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const visibleOptions = options.slice(0, 4)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (showCustom) return
      const idx = parseInt(e.key, 10) - 1
      if (idx >= 0 && idx < visibleOptions.length) {
        e.preventDefault()
        onSelect(visibleOptions[idx].value, false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [visibleOptions, onSelect, showCustom])

  useEffect(() => {
    if (showCustom && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showCustom])

  const handleCustomSubmit = () => {
    const trimmed = customValue.trim()
    if (trimmed) {
      onSelect(trimmed, true)
    }
  }

  return (
    <div className="space-y-2">
      {visibleOptions.map((opt, idx) => (
        <button
          key={opt.value}
          data-testid="scaffolded-option"
          data-value={opt.value}
          onClick={() => onSelect(opt.value, false)}
          className={cn(
            'w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors',
            selected === opt.value
              ? 'border-echo-indigo bg-indigo-50 text-deep-ink'
              : 'border-border-gray text-deep-ink hover:border-echo-indigo/50 hover:bg-indigo-50/30'
          )}
        >
          <span className="inline-flex items-center gap-3">
            <span
              className={cn(
                'w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium shrink-0',
                selected === opt.value
                  ? 'bg-echo-indigo text-white'
                  : 'bg-soft-panel text-slate-500'
              )}
            >
              {idx + 1}
            </span>
            <span className="flex-1">
              <span className="font-medium">{opt.label}</span>
              {opt.rationale && (
                <span className="block text-xs text-slate-500 mt-0.5">{opt.rationale}</span>
              )}
            </span>
          </span>
        </button>
      ))}

      {allowOther && !showCustom && (
        <button
          onClick={() => setShowCustom(true)}
          className="w-full text-left px-4 py-2.5 rounded-lg border border-dashed border-border-gray text-sm text-slate-500 hover:border-echo-indigo/50 hover:text-deep-ink transition-colors"
        >
          Something else…
        </button>
      )}

      {allowOther && showCustom && (
        <div className="rounded-lg border border-echo-indigo bg-indigo-50/30 p-3 space-y-2">
          <input
            ref={inputRef}
            data-testid="custom-option-input"
            type="text"
            className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 bg-white"
            placeholder="Describe your use case…"
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCustomSubmit()
              if (e.key === 'Escape') setShowCustom(false)
            }}
          />
          <div className="flex gap-2">
            <button
              onClick={handleCustomSubmit}
              disabled={!customValue.trim()}
              className="px-3 py-1.5 rounded-lg bg-echo-indigo text-white text-xs font-medium disabled:opacity-40"
            >
              Use this
            </button>
            <button
              onClick={() => { setShowCustom(false); setCustomValue('') }}
              className="px-3 py-1.5 rounded-lg text-slate-500 text-xs hover:text-deep-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
